import { Inject, Injectable } from '@angular/core';
import { Store } from '@ngxs/store';
import { MyModulesState } from 'app/library/my-profile-modules/models/my-modules.state';
import { Module } from 'app/library/module/models/module.model';
import { ModuleUser } from 'app/library/module-user/models/module-user.model';
import { NgxsActionHelper } from 'app/projects/core/src/lib/services/action.helper';
import { ILanguageService } from 'app/projects/shared/src/lib/interfaces/language.interface';
import { MAXBRAIN_LANGUAGE_SERVICE } from 'app/projects/shared/src/lib/services/language.token';
import {
    FetchModuleFeatures,
    FetchModuleFeaturesFailure,
    FetchModuleFeaturesSuccess,
    UpdateModuleFeature,
    UpdateModuleFeatureFailure,
    UpdateModuleFeatureSuccess,
} from '../actions';
import { ModuleFeatureSwitchName } from 'app/projects/shared/src/lib/enums/module-feature-switch.enum';
import { ModuleFeature } from '../models/module-feature.model';
import { ModuleFeatureState } from '../models/module-feature.state';
import {
    isContentTabVisibleForAdminEdit,
    isContentTabVisibleForParticipantView,
} from '../utils/module-content-tab-visibility.util';

@Injectable()
export class ModuleFeatureService {
    constructor(private _store: Store, private _actionHelper: NgxsActionHelper, @Inject(MAXBRAIN_LANGUAGE_SERVICE) public languageService: ILanguageService) {}

    async fetchModuleFeatures(moduleId: string): Promise<boolean> {
        this._store.dispatch(new FetchModuleFeatures(moduleId));

        return await this._actionHelper.race(FetchModuleFeaturesSuccess, FetchModuleFeaturesFailure).toPromise();
    }

    getModuleFeatures(): ModuleFeature[] {
        return this._store.selectSnapshot(ModuleFeatureState.getEntities);
    }

    async updateModuleFeatures(moduleId: string, moduleFeatures: ModuleFeature | ModuleFeature[]) {
        const features = Array.isArray(moduleFeatures) ? moduleFeatures : [moduleFeatures];
        this._store.dispatch(new UpdateModuleFeature({ moduleId, moduleFeatures: features }));

        return await this._actionHelper.race(UpdateModuleFeatureSuccess, UpdateModuleFeatureFailure).toPromise();
    }

    checkModuleFeatureStatus(id: string, status: string = 'active'): boolean {
        const feature = ModuleFeatureState.getEntity(this._store.selectSnapshot(ModuleFeatureState), id);

        if (!feature) {
            return false;
        }

        return feature.status === status;
    }

    isContentTabVisibleForParticipantView(module: Module): boolean {
        const feature = ModuleFeatureState.getEntity(this._store.selectSnapshot(ModuleFeatureState), ModuleFeatureSwitchName.ContentTab);

        if (!feature || !module) {
            return false;
        }

        const moduleUser = this._getCurrentModuleUser(module.id);

        return isContentTabVisibleForParticipantView(feature.status, module, moduleUser);
    }

    isContentTabVisibleForAdminEdit(module: Module): boolean {
        const feature = ModuleFeatureState.getEntity(this._store.selectSnapshot(ModuleFeatureState), ModuleFeatureSwitchName.ContentTab);

        if (!feature || !module) {
            return false;
        }

        return isContentTabVisibleForAdminEdit(feature.status, module);
    }

    private _getCurrentModuleUser(moduleId: string): ModuleUser | undefined {
        return (this._store.selectSnapshot(MyModulesState.getEntities) || []).find((entity) => entity.moduleId === moduleId);
    }
}
